/**
 * PaymentDialog — reusable payment-collection popup used by both Program
 * Assessment and Retention Fee modules. Handles:
 *   • enrolled student picker (typeahead)
 *   • optional "not yet enrolled" free-text mode (Program Assessment only)
 *   • date, amount, method, reference, notes
 *
 * Submits directly to POST /api/student-accounts with the right category
 * so payments automatically show up on the Income → Payment Received report
 * (unified via student_accounts.payments[]).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Search, X } from "lucide-react";

const PAYMENT_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "online", label: "Online Payment" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "check", label: "Bank Check" },
  { value: "card", label: "Debit / Credit Card" },
  { value: "other", label: "Other" },
];

function todayISO() { return new Date().toISOString().slice(0, 10); }
function studentLabel(s) {
  return s.full_name || `${s.name || ""} ${s.last_name || ""}`.trim() || "Unnamed student";
}

function StudentPickerInline({ students, value, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const selected = useMemo(
    () => (students || []).find((s) => s.id === value) || null,
    [students, value],
  );
  useEffect(() => { if (open) setQ(""); }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const fn = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, [open]);
  const query = q.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!query) return students || [];
    return (students || []).filter((s) => {
      const hay = [s.full_name, s.name, s.last_name, s.class_name, s.section_name].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(query);
    });
  }, [students, query]);
  return (
    <div ref={rootRef} className="relative mt-1.5">
      <button
        type="button"
        disabled={disabled}
        onClick={() => { setOpen((v) => !v); setTimeout(() => inputRef.current?.focus(), 20); }}
        className="w-full h-11 rounded-2xl border border-input bg-white px-3 text-sm flex items-center justify-between gap-2 hover:border-brand-purple/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="pay-dialog-student-select"
      >
        {selected ? (
          <span className="truncate text-foreground">
            {studentLabel(selected)}
            {selected.class_name && <span className="text-brand-inkMuted"> — {selected.class_name}</span>}
          </span>
        ) : (
          <span className="text-brand-inkMuted">Select enrolled student</span>
        )}
        <span className="flex items-center gap-1 shrink-0">
          {selected && !disabled && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onChange(""); }}
              className="text-brand-inkMuted hover:text-red-600 p-0.5 rounded"
              aria-label="Clear"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          <Search className="w-4 h-4 text-brand-inkMuted" />
        </span>
      </button>
      {open && !disabled && (
        <div className="absolute z-50 mt-1 w-full rounded-2xl border border-input bg-white shadow-floating overflow-hidden">
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search className="w-4 h-4 text-brand-inkMuted absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                ref={inputRef}
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Type student name, class, or section..."
                className="w-full h-10 rounded-xl border border-input bg-white pl-9 pr-3 text-sm focus:outline-none focus:border-brand-purple/60"
                data-testid="pay-dialog-student-search"
              />
            </div>
          </div>
          <ul className="max-h-64 overflow-y-auto">
            {filtered.length === 0 ? (
              <li className="px-3 py-6 text-center text-sm text-brand-inkMuted">No students match</li>
            ) : (
              filtered.slice(0, 200).map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => { onChange(s.id); setOpen(false); }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-brand-purpleLight/40 ${s.id === value ? "bg-brand-purpleLight/60" : ""}`}
                    data-testid={`pay-dialog-student-option-${s.id}`}
                  >
                    <div className="font-medium">{studentLabel(s)}</div>
                    {(s.class_name || s.section_name) && (
                      <div className="text-xs text-brand-inkMuted">
                        {[s.class_name, s.section_name].filter(Boolean).join(" · ")}
                      </div>
                    )}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function PaymentDialog({
  open, onClose, onSaved,
  category,               // 'program_assessment' | 'retention_fee'
  branchId,
  students,               // enrolled students list (may include name/last_name/class_name)
  currency = "₱",
  allowUnenrolled = false, // true for program_assessment
  title,
  description,
}) {
  const [studentId, setStudentId] = useState("");
  const [unenrolledMode, setUnenrolledMode] = useState(false);
  const [unenrolledName, setUnenrolledName] = useState("");
  const [date, setDate] = useState(todayISO());
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [refNo, setRefNo] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setStudentId(""); setUnenrolledMode(false); setUnenrolledName("");
      setDate(todayISO()); setAmount(""); setMethod("cash");
      setRefNo(""); setNotes("");
    }
  }, [open, category]);

  if (!open) return null;

  async function submit(e) {
    e.preventDefault();
    const amt = Number(amount || 0);
    if (!amt || amt <= 0) { toast.error("Enter a positive payment amount"); return; }
    if (unenrolledMode) {
      if (!unenrolledName.trim()) { toast.error("Enter the student's name"); return; }
    } else if (!studentId) {
      toast.error("Pick a student"); return;
    }
    setSaving(true);
    try {
      const body = {
        branch_id: branchId,
        student_id: unenrolledMode ? "" : studentId,
        unenrolled_student_name: unenrolledMode ? unenrolledName.trim() : "",
        category,
        date,
        description: category === "program_assessment" ? "Program Assessment Payment" : "Retention Fee Payment",
        amount: amt,
        paid_amount: amt,
        notes: notes.trim(),
        source: "outside",
        payment_method: method,
        reference_no: refNo.trim(),
        payment_date: date,
      };
      await api.post("/student-accounts", body);
      toast.success("Payment recorded and posted to Income");
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent className="rounded-3xl max-w-md" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl uppercase tracking-tight">{title || "Record Payment"}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4" data-testid="payment-dialog-form">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="font-heading">Student <span className="text-[#C62828]">*</span></Label>
              {allowUnenrolled && (
                <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={unenrolledMode}
                    onChange={(e) => setUnenrolledMode(e.target.checked)}
                    className="w-3.5 h-3.5 accent-brand-purple"
                    data-testid="payment-dialog-unenrolled-toggle"
                  />
                  <span className="text-brand-inkMuted">Not yet enrolled</span>
                </label>
              )}
            </div>
            {unenrolledMode ? (
              <Input
                value={unenrolledName}
                onChange={(e) => setUnenrolledName(e.target.value)}
                placeholder="Full name of applicant"
                className="rounded-2xl h-11"
                data-testid="payment-dialog-unenrolled-name"
              />
            ) : (
              <StudentPickerInline students={students} value={studentId} onChange={setStudentId} />
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="font-heading">Date of Payment <span className="text-[#C62828]">*</span></Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-2xl h-11 mt-1.5" data-testid="payment-dialog-date" />
            </div>
            <div>
              <Label className="font-heading">Amount ({currency}) <span className="text-[#C62828]">*</span></Label>
              <Input
                type="number" min="0" step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="rounded-2xl h-11 mt-1.5 tabular-nums"
                data-testid="payment-dialog-amount"
              />
            </div>
            <div>
              <Label className="font-heading">Method of Payment <span className="text-[#C62828]">*</span></Label>
              <select
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                className="mt-1.5 w-full h-11 rounded-2xl border border-input bg-white px-3 text-sm"
                data-testid="payment-dialog-method"
              >
                {PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="font-heading">Reference No.</Label>
              <Input
                value={refNo}
                onChange={(e) => setRefNo(e.target.value)}
                placeholder="OR / Transaction ID"
                className="rounded-2xl h-11 mt-1.5"
                data-testid="payment-dialog-reference"
              />
            </div>
          </div>

          <div>
            <Label className="font-heading">Additional Notes / Remarks</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              maxLength={1000}
              placeholder="Optional notes about this payment"
              className="rounded-2xl mt-1.5"
              data-testid="payment-dialog-notes"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="rounded-2xl" data-testid="payment-dialog-cancel">Cancel</Button>
            <Button
              type="submit"
              disabled={saving}
              className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white"
              data-testid="payment-dialog-save"
            >
              {saving ? "Saving..." : "Save Payment"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
